import { AnimatedBox } from '@apeiron/temp'
import { ApeironBattleDemoTitle } from '@apeiron/ui'
import { styled, useMediaQuery } from '@mui/material'
import BackgroundJPG from '@public/images/background-battle-demo.jpg'
import BottomLeftPNG from '@public/images/image-battle-demo-left-bottom.png'
import TopLeftPNG from '@public/images/image-battle-demo-left-top.png'
import MobileBottomLeftPNG from '@public/images/image-battle-demo-mobile-left-bottom.png'
import MobileTopLeftPNG from '@public/images/image-battle-demo-mobile-left-top.png'
import MobileBottomRightPNG from '@public/images/image-battle-demo-mobile-right-bottom.png'
import MobileTopRightPNG from '@public/images/image-battle-demo-mobile-right-top.png'
import BottomRightPNG from '@public/images/image-battle-demo-right-bottom.png'
import TopRightPNG from '@public/images/image-battle-demo-right-top.png'
import BattleDemoCheaterWarning from '@src/components/battle/demo/leaderboard/BattleDemoCheaterWarning'
import BattleDemoGuildWarPvPLeaderboard from '@src/components/battle/demo/leaderboard/BattleDemoGuildWarPvPLeaderboard'
import BattleDemoSecNavBar from '@src/components/navigationBar/secondary/BattleDemoSecNavBar'
import ApeironCorneredBox from '@src/components/share/apeiron/ApeironCorneredBox'
import { ROUTE } from '@src/constants/route'
import { GuildChangeProvider } from '@src/contexts/guild/GuildChangeContext'
import { useSecondaryNavBarContext } from '@src/contexts/share/SecondaryNavBarContext'
import theme from '@src/styles/theme'
import type { NextPage } from 'next'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

const Container = styled(ApeironCorneredBox)`
  height: calc(100vh - 116px);
  width: 100%;

  ${props => props.theme.breakpoints.up('res1024')} {
    background-image: url(${BackgroundJPG.src});
    background-size: cover;
    background-position: center;
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    background: linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.7)),
      url(${BackgroundJPG.src});
    background-size: cover;
    background-position: center bottom 60%;
  }

  .Custom-Container {
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    position: relative;
    ${props => props.theme.breakpoints.up('res1024')} {
      gap: 80px;
    }
    ${props => props.theme.breakpoints.down('res1024')} {
      gap: 30px;
    }
  }
  .Custom-Corner-Container {
    transform: rotate(180deg);
  }
  .Custom-Top-Left {
    aspect-ratio: 0.52;
    max-width: 150px;
    width: 100%;
  }
  .Custom-Bottom-Left {
    aspect-ratio: 0.545;
    max-width: 350px;
    width: 100%;
  }
  .Custom-Top-Right {
    aspect-ratio: 0.545;
    max-width: 120px;
    width: 100%;
  }
  .Custom-Bottom-Right {
    aspect-ratio: 1.755;
    bottom: -55px;
    max-width: 1000px;
    width: 100%;
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    .Custom-Top-Left {
      aspect-ratio: 0.883;
      max-width: 218px;
      width: 100%;
    }
    .Custom-Bottom-Left {
      aspect-ratio: 0.382;
      max-width: 42px;
      width: 100%;
    }
    .Custom-Top-Right {
      aspect-ratio: 0.765;
      max-width: 85px;
      width: 100%;
    }
    .Custom-Bottom-Right {
      aspect-ratio: 0.742;
      max-width: 319px;
      width: 100%;
    }
  }
`

const StyledBattleDemoTitle = styled(ApeironBattleDemoTitle)`
  ${props => props.theme.breakpoints.up('res1024')} {
    margin-top: 45px;
    margin-left: 90px;
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    margin-top: 40px;
    margin-left: 20px;
  }
  .Custom-Subtitle {
    font-size: 20px;
    font-weight: 300;
  }
`

const FadeInContainer = styled(AnimatedBox)`
  align-items: center;
  display: flex;
  flex-direction: column;
  flex: 1;
  justify-content: flex-start;
  width: 100%;
  margin-bottom: 40px;
`

const Leaderboard = styled(BattleDemoGuildWarPvPLeaderboard)`
  width: 95%;
  min-height: 690px;
`

const GuildWarPvPPage: NextPage<Props> = () => {
  const { setupBar } = useSecondaryNavBarContext()

  const { t } = useTranslation()

  const mobile = useMediaQuery(theme.breakpoints.down('res1024'))

  const [cornerAnimated, setCornerAnimated] = useState<boolean>(false)

  const handleOnAnimationEnd = useCallback(() => {
    setCornerAnimated(true)
  }, [])

  useEffect(() => {
    setupBar(<BattleDemoSecNavBar route={ROUTE.BATTLE_DEMO_GUILD_WAR_PVP} />)

    return () => setupBar(null)
  }, [setupBar])

  return (
    <Container
      bottomLeft={mobile ? MobileBottomLeftPNG.src : BottomLeftPNG.src}
      bottomRight={mobile ? MobileBottomRightPNG.src : BottomRightPNG.src}
      topLeft={mobile ? MobileTopLeftPNG.src : TopLeftPNG.src}
      topRight={mobile ? MobileTopRightPNG.src : TopRightPNG.src}
      onAnimationEnd={handleOnAnimationEnd}
    >
      <StyledBattleDemoTitle
        title={t('battle_demo.guild_war_pvp')}
        subtitle={t('battle_demo.cross_leaderboard')}
      />
      <FadeInContainer in={cornerAnimated}>
        <GuildChangeProvider>
          <Leaderboard />
        </GuildChangeProvider>
      </FadeInContainer>
      <BattleDemoCheaterWarning />
    </Container>
  )
}

type Props = {}

export default GuildWarPvPPage
